import React, { useEffect, useMemo, useRef, useState } from "react";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { ButtonComponent } from "@syncfusion/ej2-react-buttons";

import securityService from "../security/security.service";
import changePasswordService from "./change-password.service";
import "./change-password.scss";

/**
 * Safe class join: removes empty/undefined tokens so Syncfusion never receives empty token.
 */
const cx = (...tokens) =>
  tokens.map((t) => String(t || "").trim()).filter(Boolean).join(" ");

const UPDATE_BTN_ID = "cpUpdatePasswordBtn";

const ChangePasswordSyncfusion = () => {
  const userId = securityService.getLoggedInUserId();

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  const [oldOk, setOldOk] = useState(false);
  const [checkingOld, setCheckingOld] = useState(false);

  const [errors, setErrors] = useState({
    old: "",
    New: "",
    confirm: "",
    submit: "",
    success: "",
  });

  // Syncfusion TextBox refs
  const oldRef = useRef(null);
  const newRef = useRef(null);
  const confirmRef = useRef(null);

  const oldPwdCheckSeq = useRef(0);

  const focusSf = (ref) => {
    requestAnimationFrame(() => {
      const el = ref?.current?.element; // actual <input>
      el?.focus?.();
      el?.select?.();
    });
  };

  const clearMessages = () => {
    setErrors((p) => ({ ...p, submit: "", success: "" }));
  };

  // Focus first invalid field (used when TAB on soft-disabled button)
  const focusFirstInvalid = () => {
    // Prefer fields that are invalid by actual values (not only errors text),
    // because errors may not be set until blur.
    if (!oldOk || errors.old) {
      focusSf(oldRef);
      return;
    }
    if (!newPwd.trim() || errors.New) {
      focusSf(newRef);
      return;
    }
    if (!confirmPwd.trim() || newPwd !== confirmPwd || errors.confirm) {
      focusSf(confirmRef);
      return;
    }
    focusSf(oldRef);
  };

  const validateOld = async (pwdArg) => {
    clearMessages();

    const pwd = (pwdArg ?? oldPwd ?? "").trim();
    if (!pwd) {
      setOldOk(false);
      setErrors((p) => ({ ...p, old: "Old password is required." }));
      focusSf(oldRef);
      return false;
    }

    setCheckingOld(true);
    setErrors((p) => ({ ...p, old: "" }));

    const seq = ++oldPwdCheckSeq.current;

    try {
      const res = await changePasswordService.checkOldPassword(pwd, userId);

      // Ignore stale response
      if (seq !== oldPwdCheckSeq.current) return false;

      const ok = (res?.data?.recordset?.length || 0) > 0;

      if (ok) {
        setOldOk(true);
        setErrors((p) => ({ ...p, old: "" }));
        requestAnimationFrame(() => focusSf(newRef));
        return true;
      } else {
        setOldOk(false);
        setNewPwd("");
        setConfirmPwd("");
        setErrors((p) => ({ ...p, old: "Old password is incorrect." }));
        requestAnimationFrame(() => focusSf(oldRef));
        return false;
      }
    } catch (e) {
      setOldOk(false);
      setErrors((p) => ({ ...p, old: "Unable to verify old password. Try again." }));
      requestAnimationFrame(() => focusSf(oldRef));
      return false;
    } finally {
      setCheckingOld(false);
    }
  };

  const validateNewOnBlur = () => {
    clearMessages();
    if (!oldOk) return;

    if (!newPwd.trim()) {
      setErrors((p) => ({ ...p, New: "New password is required." }));
      return;
    }
    setErrors((p) => ({ ...p, New: "" }));
  };

  const validateConfirmOnBlur = () => {
    clearMessages();
    if (!oldOk) return;

    if (!confirmPwd.trim()) {
      setErrors((p) => ({ ...p, confirm: "Confirm password is required." }));
      return;
    }

    if (newPwd !== confirmPwd) {
      setErrors((p) => ({ ...p, confirm: "Confirm password does not match." }));
      return;
    }

    setErrors((p) => ({ ...p, confirm: "" }));
  };

  const canSubmit = useMemo(() => {
    if (!oldOk) return false;
    if (!newPwd.trim()) return false;
    if (!confirmPwd.trim()) return false;
    if (newPwd !== confirmPwd) return false;
    return true;
  }, [oldOk, newPwd, confirmPwd]);

  const onSubmit = async () => {
    clearMessages();

    if (!canSubmit) {
      setErrors((p) => ({ ...p, submit: "Please correct the errors above." }));
      return;
    }

    try {
      await changePasswordService.updatePassword(newPwd, userId);

      setErrors((p) => ({
        ...p,
        submit: "",
        success: "Password changed successfully.",
      }));

      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setOldOk(false);

      requestAnimationFrame(() => focusSf(oldRef));
    } catch (e) {
      setErrors((p) => ({
        ...p,
        submit: "Password update failed. Please try again.",
        success: "",
      }));
    }
  };

  useEffect(() => {
    focusSf(oldRef);
  }, []);

  /**
   * HARD FIX:
   * Capture TAB on the *actual DOM button* (Syncfusion renders real <button>)
   * If soft-disabled (canSubmit=false) AND button is focused,
   * prevent TAB from leaving app and send focus to invalid field.
   */
  useEffect(() => {
    const handler = (ev) => {
      if (ev.key !== "Tab") return;
      if (canSubmit) return;

      const btnEl = document.getElementById(UPDATE_BTN_ID);
      if (!btnEl) return;

      if (document.activeElement === btnEl) {
        ev.preventDefault();
        ev.stopPropagation();

        if (ev.shiftKey) {
          // Shift+Tab from button -> go back to Confirm (normal UX)
          focusSf(confirmRef);
        } else {
          // Tab from button -> go to first invalid field
          focusFirstInvalid();
        }
      }
    };

    // Capture mode = true (runs before Syncfusion / browser default)
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [canSubmit, oldOk, newPwd, confirmPwd, errors]);

  return (
    <div
  className="content-wrapper"
  style={{
    // Use dynamic viewport height (better on Chrome)
    minHeight: "calc(100dvh - 120px)", // adjust 120px if your header is taller
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: 15,
    overflow: "hidden",  // stop tiny overflow scrollbar
    boxSizing: "border-box",
  }}
>
<div
  className="cp-card"
  style={{
    width: "100%",
    maxWidth: 520,
    padding: 18,
    borderRadius: 10,
    background: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,1)",
    boxSizing: "border-box",     // IMPORTANT
    marginTop: 15,               // move your marginTop here instead of wrapper
  }}
>

        <h3 style={{ margin: 0, marginBottom: 16, textAlign: "center" }}>
          CHANGE PASSWORD
        </h3>

        {/* Old Password */}
        <div style={{ marginBottom: 14 }}>
          <label className="e-label" style={{ display: "block", marginBottom: 6 }}>
            Old Password
          </label>

          <TextBoxComponent
            ref={oldRef}
            type="password"
            placeholder="Enter old password"
            value={oldPwd}
            input={(e) => {
              setOldPwd(e.value || "");
              setOldOk(false);
              setNewPwd("");
              setConfirmPwd("");
              setErrors({ old: "", New: "", confirm: "", submit: "", success: "" });
            }}
            blur={() => validateOld()}
          />

          {checkingOld ? (
            <div style={{ marginTop: 6, fontSize: 14, opacity: 0.8 }}>
              Checking old password...
            </div>
          ) : null}

          {errors.old ? (
            <div style={{ marginTop: 6, color: "#d32f2f", fontSize: 16 }}>
              {errors.old}
            </div>
          ) : null}
        </div>

        {/* New Password */}
        <div style={{ marginBottom: 14 }}>
          <label className="e-label" style={{ display: "block", marginBottom: 6 }}>
            New Password
          </label>

          <TextBoxComponent
            ref={newRef}
            type="password"
            enabled={oldOk}
            placeholder="Enter new password"
            value={newPwd}
            input={(e) => {
              setNewPwd(e.value || "");
              setErrors((p) => ({ ...p, New: "", confirm: "", submit: "", success: "" }));
            }}
            blur={validateNewOnBlur}
          />

          {errors.New ? (
            <div style={{ marginTop: 6, color: "#d32f2f", fontSize: 14 }}>
              {errors.New}
            </div>
          ) : null}
        </div>

        {/* Confirm Password */}
        <div style={{ marginBottom: 18 }}>
          <label className="e-label" style={{ display: "block", marginBottom: 6 }}>
            Confirm Password
          </label>

          <TextBoxComponent
            ref={confirmRef}
            type="password"
            enabled={oldOk}
            placeholder="Re-enter new password"
            value={confirmPwd}
            input={(e) => {
              setConfirmPwd(e.value || "");
              setErrors((p) => ({ ...p, confirm: "", submit: "", success: "" }));
            }}
            blur={validateConfirmOnBlur}
          />

          {errors.confirm ? (
            <div style={{ marginTop: 6, color: "#d32f2f", fontSize: 14 }}>
              {errors.confirm}
            </div>
          ) : null}
        </div>

        {errors.submit ? (
          <div style={{ marginBottom: 6, color: "#d32f2f", fontSize: 14 }}>
            {errors.submit}
          </div>
        ) : null}

        {errors.success ? (
          <div style={{ marginBottom: 6, color: "#2e7d32", fontSize: 14 }}>
            {errors.success}
          </div>
        ) : null}

        {/* IMPORTANT: keep disabled={false} so button is focusable.
            Add id for DOM capture (TAB fix). */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ButtonComponent
            id={UPDATE_BTN_ID}
            cssClass={cx("e-primary", !canSubmit && "cp-soft-disabled")}
            disabled={false}
            content="Update Password"
            onClick={onSubmit}
          />
        </div>

        {!canSubmit ? (
          <div style={{ marginTop: 10, fontSize: 14, opacity: 0.9, textAlign: "center" }}>
            Fill all fields correctly to enable update.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ChangePasswordSyncfusion;
